import type {
  AgentRuntimeAttachedPullRequest,
  AgentRuntimePreferredMcpServerRef,
  AgentRuntimeRequestedSkillRef,
  AgentRuntimeSessionContextRef,
} from "@cord/shared"
import { normalizeSessionContextSummaryIntent } from "@/lib/session-context-refs"

const HIDDEN_SESSION_CONTEXT_START = "[cord-session-context]"
const HIDDEN_SESSION_CONTEXT_END = "[/cord-session-context]"
const HIDDEN_SESSION_CONTEXT_REF_PREFIX = "ref::"
const HIDDEN_ATTACHED_PR_START = "[cord-attached-pr]"
const HIDDEN_ATTACHED_PR_END = "[/cord-attached-pr]"
const HIDDEN_ATTACHED_PR_PREFIX = "pr::"
const HIDDEN_REQUESTED_SKILLS_START = "[cord-requested-skills]"
const HIDDEN_REQUESTED_SKILLS_END = "[/cord-requested-skills]"
const HIDDEN_REQUESTED_SKILLS_PREFIX = "skill::"
const HIDDEN_PREFERRED_MCP_SERVERS_START = "[cord-preferred-mcp-servers]"
const HIDDEN_PREFERRED_MCP_SERVERS_END = "[/cord-preferred-mcp-servers]"
const HIDDEN_PREFERRED_MCP_SERVERS_PREFIX = "mcp::"
const HIDDEN_CLIENT_MESSAGE_ID_START = "[cord-client-message-id]"
const HIDDEN_CLIENT_MESSAGE_ID_END = "[/cord-client-message-id]"
const HIDDEN_CLIENT_MESSAGE_ID_PREFIX = "cid::"
const HIDDEN_INTERACTIVE_FALLBACK_START = "[cord-interactive-fallback]"
const HIDDEN_INTERACTIVE_FALLBACK_END = "[/cord-interactive-fallback]"

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

const HIDDEN_SESSION_CONTEXT_BLOCK_RE = new RegExp(
  `${escapeRegExp(HIDDEN_SESSION_CONTEXT_START)}[\\s\\S]*?${escapeRegExp(HIDDEN_SESSION_CONTEXT_END)}\\n?`,
  "g",
)
const HIDDEN_ATTACHED_PR_BLOCK_RE = new RegExp(
  `${escapeRegExp(HIDDEN_ATTACHED_PR_START)}[\\s\\S]*?${escapeRegExp(HIDDEN_ATTACHED_PR_END)}\\n?`,
  "g",
)
const HIDDEN_REQUESTED_SKILLS_BLOCK_RE = new RegExp(
  `${escapeRegExp(HIDDEN_REQUESTED_SKILLS_START)}[\\s\\S]*?${escapeRegExp(HIDDEN_REQUESTED_SKILLS_END)}\\n?`,
  "g",
)
const HIDDEN_PREFERRED_MCP_SERVERS_BLOCK_RE = new RegExp(
  `${escapeRegExp(HIDDEN_PREFERRED_MCP_SERVERS_START)}[\\s\\S]*?${escapeRegExp(HIDDEN_PREFERRED_MCP_SERVERS_END)}\\n?`,
  "g",
)
const HIDDEN_CLIENT_MESSAGE_ID_BLOCK_RE = new RegExp(
  `${escapeRegExp(HIDDEN_CLIENT_MESSAGE_ID_START)}[\\s\\S]*?${escapeRegExp(HIDDEN_CLIENT_MESSAGE_ID_END)}\\n?`,
  "g",
)
const HIDDEN_INTERACTIVE_FALLBACK_BLOCK_RE = new RegExp(
  `${escapeRegExp(HIDDEN_INTERACTIVE_FALLBACK_START)}[\\s\\S]*?${escapeRegExp(HIDDEN_INTERACTIVE_FALLBACK_END)}\\n?`,
  "g",
)
const LEGACY_SESSION_CONTEXT_NOTE_RE = /\[session-context-note:[^\]]*\]\n?/g
const LEGACY_SESSION_CONTEXT_REF_RE = /\[session-context-ref:[^\]]*\]\n?/g

// The hidden ref transport is pipe-delimited as
// ref::referenceId|sessionId|repositoryId|title|conversation=...|intent=...
// Keep the field positions named so future protocol extensions do not silently
// mix up identity fields with metadata suffixes.
const SESSION_CONTEXT_REF_REFERENCE_ID_FIELD_INDEX = 0
const SESSION_CONTEXT_REF_SESSION_ID_FIELD_INDEX = 1
const SESSION_CONTEXT_REF_REPOSITORY_ID_FIELD_INDEX = 2
const SESSION_CONTEXT_REF_TITLE_FIELD_INDEX = 3
const SESSION_CONTEXT_REF_METADATA_START_FIELD_INDEX = 4

function extractContextRefPayload(line: string): string | null {
  const trimmed = line.trim()
  if (!trimmed) return null

  const normalized = trimmed.replace(/^(?:(?:>\s*)|(?:[-*+]\s+)|(?:\d+\.\s+))+/, "")
  const prefixIndex = normalized.indexOf(HIDDEN_SESSION_CONTEXT_REF_PREFIX)
  if (prefixIndex === -1) return null

  return normalized.slice(prefixIndex + HIDDEN_SESSION_CONTEXT_REF_PREFIX.length)
}

function extractAttachedPullRequestPayload(line: string): string | null {
  const trimmed = line.trim()
  if (!trimmed) return null

  const normalized = trimmed.replace(/^(?:(?:>\s*)|(?:[-*+]\s+)|(?:\d+\.\s+))+/, "")
  const prefixIndex = normalized.indexOf(HIDDEN_ATTACHED_PR_PREFIX)
  if (prefixIndex === -1) return null

  return normalized.slice(prefixIndex + HIDDEN_ATTACHED_PR_PREFIX.length)
}

function extractClientMessageIdPayload(line: string): string | null {
  const trimmed = line.trim()
  if (!trimmed) return null

  const normalized = trimmed.replace(/^(?:(?:>\s*)|(?:[-*+]\s+)|(?:\d+\.\s+))+/, "")
  const prefixIndex = normalized.indexOf(HIDDEN_CLIENT_MESSAGE_ID_PREFIX)
  if (prefixIndex === -1) return null

  return normalized.slice(prefixIndex + HIDDEN_CLIENT_MESSAGE_ID_PREFIX.length)
}

function extractRequestedSkillPayload(line: string): string | null {
  const trimmed = line.trim()
  if (!trimmed) return null

  const normalized = trimmed.replace(/^(?:(?:>\s*)|(?:[-*+]\s+)|(?:\d+\.\s+))+/, "")
  const prefixIndex = normalized.indexOf(HIDDEN_REQUESTED_SKILLS_PREFIX)
  if (prefixIndex === -1) return null

  return normalized.slice(prefixIndex + HIDDEN_REQUESTED_SKILLS_PREFIX.length)
}

function extractPreferredMcpServerPayload(line: string): string | null {
  const trimmed = line.trim()
  if (!trimmed) return null

  const normalized = trimmed.replace(/^(?:(?:>\s*)|(?:[-*+]\s+)|(?:\d+\.\s+))+/, "")
  const prefixIndex = normalized.indexOf(HIDDEN_PREFERRED_MCP_SERVERS_PREFIX)
  if (prefixIndex === -1) return null

  return normalized.slice(prefixIndex + HIDDEN_PREFERRED_MCP_SERVERS_PREFIX.length)
}

export function stripHiddenSessionContextTransport(text: string): string {
  if (!text) return text

  return text
    .replace(HIDDEN_SESSION_CONTEXT_BLOCK_RE, "")
    .replace(HIDDEN_ATTACHED_PR_BLOCK_RE, "")
    .replace(HIDDEN_REQUESTED_SKILLS_BLOCK_RE, "")
    .replace(HIDDEN_PREFERRED_MCP_SERVERS_BLOCK_RE, "")
    .replace(HIDDEN_CLIENT_MESSAGE_ID_BLOCK_RE, "")
    .replace(HIDDEN_INTERACTIVE_FALLBACK_BLOCK_RE, "")
    .replace(LEGACY_SESSION_CONTEXT_NOTE_RE, "")
    .replace(LEGACY_SESSION_CONTEXT_REF_RE, "")
    .replace(/\n{3,}/g, "\n\n")
    .trimEnd()
}

function decodeMaybeUriComponent(value: string): string | null {
  if (!value) return null
  try {
    const decoded = decodeURIComponent(value)
    return decoded.trim().length > 0 ? decoded : null
  } catch {
    return value.trim().length > 0 ? value : null
  }
}

function parseHiddenSessionContextRefPayload(raw: string): AgentRuntimeSessionContextRef | null {
  const fields = raw.split("|")
  const referenceId = fields[SESSION_CONTEXT_REF_REFERENCE_ID_FIELD_INDEX]?.trim()
  const sessionId = fields[SESSION_CONTEXT_REF_SESSION_ID_FIELD_INDEX]?.trim()
  if (!referenceId || !sessionId) return null

  let conversationId: string | null | undefined
  let summaryIntent: AgentRuntimeSessionContextRef["summaryIntent"]
  for (const metadataField of fields.slice(SESSION_CONTEXT_REF_METADATA_START_FIELD_INDEX)) {
    const separatorIndex = metadataField.indexOf("=")
    const metadataKey = separatorIndex === -1
      ? metadataField
      : metadataField.slice(0, separatorIndex)
    const metadataValue = separatorIndex === -1
      ? ""
      : metadataField.slice(separatorIndex + "=".length)
    if (metadataKey === "conversation") {
      conversationId = decodeMaybeUriComponent(metadataValue)
    } else if (metadataKey === "scope" && metadataValue === "session") {
      conversationId = null
    } else if (metadataKey === "intent") {
      summaryIntent = normalizeSessionContextSummaryIntent(metadataValue) === "code_review"
        ? "code_review"
        : undefined
    }
  }

  return {
    referenceId,
    sessionId,
    repositoryId:
      decodeMaybeUriComponent(fields[SESSION_CONTEXT_REF_REPOSITORY_ID_FIELD_INDEX] ?? "") ?? null,
    title:
      decodeMaybeUriComponent(fields[SESSION_CONTEXT_REF_TITLE_FIELD_INDEX] ?? "") ?? null,
    ...(conversationId !== undefined ? { conversationId } : {}),
    ...(summaryIntent ? { summaryIntent } : {}),
  }
}

export function extractHiddenMessageLinkage(text: string): {
  text: string
  contextRefs: AgentRuntimeSessionContextRef[]
  requestedSkillRefs: AgentRuntimeRequestedSkillRef[]
  preferredMcpServerRefs: AgentRuntimePreferredMcpServerRef[]
  clientMessageId?: string
  attachedPullRequest?: AgentRuntimeAttachedPullRequest
} {
  if (!text) {
    return { text, contextRefs: [], requestedSkillRefs: [], preferredMcpServerRefs: [] }
  }

  const contextRefs: AgentRuntimeSessionContextRef[] = []
  const blocks = text.match(HIDDEN_SESSION_CONTEXT_BLOCK_RE) ?? []
  for (const block of blocks) {
    const lines = block.split("\n")
    for (const line of lines) {
      const raw = extractContextRefPayload(line)
      if (!raw) continue
      const contextRef = parseHiddenSessionContextRefPayload(raw)
      if (contextRef) contextRefs.push(contextRef)
    }
  }

  let attachedPullRequest: AgentRuntimeAttachedPullRequest | undefined
  const attachedPullRequestBlocks = text.match(HIDDEN_ATTACHED_PR_BLOCK_RE) ?? []
  for (const block of attachedPullRequestBlocks) {
    const lines = block.split("\n")
    for (const line of lines) {
      const raw = extractAttachedPullRequestPayload(line)
      if (!raw) continue
      const [numberRaw, urlRaw, titleRaw, branchNameRaw] = raw.split("|")
      const number = Number(numberRaw?.trim() ?? "")
      if (!Number.isFinite(number)) continue
      attachedPullRequest = {
        number: Math.max(1, Math.floor(number)),
        url: decodeMaybeUriComponent(urlRaw ?? ""),
        title: decodeMaybeUriComponent(titleRaw ?? ""),
        branchName: decodeMaybeUriComponent(branchNameRaw ?? ""),
      }
    }
  }

  const requestedSkillRefs: AgentRuntimeRequestedSkillRef[] = []
  const requestedSkillBlocks = text.match(HIDDEN_REQUESTED_SKILLS_BLOCK_RE) ?? []
  for (const block of requestedSkillBlocks) {
    const lines = block.split("\n")
    for (const line of lines) {
      const raw = extractRequestedSkillPayload(line)
      if (!raw) continue
      const [skillNameRaw, descriptionRaw, sourceTypeRaw, scopeKindRaw] = raw.split("|")
      const skillName = decodeMaybeUriComponent(skillNameRaw ?? "")
      if (!skillName) continue
      requestedSkillRefs.push({
        skillName,
        description: decodeMaybeUriComponent(descriptionRaw ?? "") ?? undefined,
        sourceType:
          sourceTypeRaw === "managed" || sourceTypeRaw === "repo_native"
            ? sourceTypeRaw
            : undefined,
        scopeKind:
          scopeKindRaw === "org" || scopeKindRaw === "repo" || scopeKindRaw === "user"
            ? scopeKindRaw
            : undefined,
      })
    }
  }

  const preferredMcpServerRefs: AgentRuntimePreferredMcpServerRef[] = []
  const preferredMcpBlocks = text.match(HIDDEN_PREFERRED_MCP_SERVERS_BLOCK_RE) ?? []
  for (const block of preferredMcpBlocks) {
    const lines = block.split("\n")
    for (const line of lines) {
      const raw = extractPreferredMcpServerPayload(line)
      if (!raw) continue
      const [serverKeyRaw, displayNameRaw, descriptionRaw, upstreamUrlRaw] = raw.split("|")
      const serverKey = decodeMaybeUriComponent(serverKeyRaw ?? "")
      if (!serverKey) continue
      preferredMcpServerRefs.push({
        serverKey,
        displayName: decodeMaybeUriComponent(displayNameRaw ?? "") ?? undefined,
        description: decodeMaybeUriComponent(descriptionRaw ?? "") ?? undefined,
        upstreamUrl: decodeMaybeUriComponent(upstreamUrlRaw ?? "") ?? undefined,
      })
    }
  }

  let clientMessageId: string | undefined
  const clientMessageIdBlocks = text.match(HIDDEN_CLIENT_MESSAGE_ID_BLOCK_RE) ?? []
  for (const block of clientMessageIdBlocks) {
    const lines = block.split("\n")
    for (const line of lines) {
      const raw = extractClientMessageIdPayload(line)?.trim()
      if (!raw) continue
      clientMessageId = raw
    }
  }

  return {
    text: stripHiddenSessionContextTransport(text),
    contextRefs,
    requestedSkillRefs,
    preferredMcpServerRefs,
    clientMessageId,
    attachedPullRequest,
  }
}

export function extractHiddenSessionContextRefs(text: string): {
  text: string
  contextRefs: AgentRuntimeSessionContextRef[]
} {
  const extracted = extractHiddenMessageLinkage(text)
  return {
    text: extracted.text,
    contextRefs: extracted.contextRefs,
  }
}
